import { PenIcon } from "@xola/icons";
import { Button, getSymbol, InlineValuePopover, Input, Label, PopoverList, Select, SubmitButton } from "@xola/ui-kit";
import { SwitchField } from "common";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import {
    Affiliate,
    AffiliateDeposit,
    AffiliateDepositAmountType,
    AffiliateDepositType,
    AffiliateDepositTypeCopy,
    Permission,
    ProductObject,
    SelectedExperiences,
    SelectionState,
} from "types";
import { ScrollToFieldError } from "../../helpers/validationHelper";
import { useSeller } from "../../hooks/useAccount";
import { getSelectedExperiences, getSelectionState } from "../../models/SelectionState";
import { ProductSelectorModal } from "../Common/ProductSelectorModal";
import { SecondaryButton } from "../Common/SecondaryButton";
import { SelectedProductList } from "../Common/SelectedProductList";
import { XolaFormik } from "../Common/XolaFormik";
import { ContentLayout } from "../Layout/ContentLayout";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useLocalizationStore } from "@/stores/localizationStore";

export interface AgentPurchaseSettingsFormValues {
    selectedExperiences?: SelectedExperiences;
    affiliateNotification: {
        enabled: boolean;
    };
    travelerNotification: {
        enabled: boolean;
    };
    deposit: AffiliateDeposit;
    voucher: boolean;
}

interface AgentPurchaseSettingsFormProps {
    isLoading: boolean;
    agent: Affiliate;
    validationSchema: any;
    initialValues: AgentPurchaseSettingsFormValues;
    onFormSubmit: (values: AgentPurchaseSettingsFormValues) => void;
}

export const AgentPurchaseSettingsForm = ({
    isLoading,
    agent,
    validationSchema,
    initialValues,
    onFormSubmit,
}: AgentPurchaseSettingsFormProps) => {
    const seller = useSeller();
    const history = useHistory();
    const locale = useLocalizationStore().getLocale();

    const { isGranted } = useAuthorization();
    const sellerCurrencySymbol = getSymbol(seller.currency, locale);
    const isAffiliateLoginEnabled = seller.preferences.affiliate?.allowLogin;
    const isDelegated = (agent?.delegated && !!agent?.user?.id) ?? false;
    const [isOpenDepositAmountPopover, setIsOpenDepositAmountPopover] = useState(false);
    const [isOpenExperienceSelectorModal, setIsOpenExperienceSelectorModal] = useState(false);
    const [depositTypeSelectorText, setDepositTypeSelectorText] = useState(
        getDepositTypeSelectorText(sellerCurrencySymbol, initialValues.deposit),
    );

    const toggleExperienceSelectorModal = () => {
        setIsOpenExperienceSelectorModal(!isOpenExperienceSelectorModal);
    };

    const handleCancelClick = () => {
        history.push(`/distribution/agents`);
    };

    return (
        <XolaFormik
            enableReinitialize
            validationSchema={validationSchema}
            initialValues={initialValues}
            onSubmit={onFormSubmit}
        >
            {({ handleSubmit, dirty, values, errors, setFieldValue }: any) => {
                const handleDepositTypeChange = (depositType: AffiliateDepositType) => {
                    const depositValue = { ...values.deposit };
                    depositValue.depositType = depositType;
                    switch (depositType) {
                        case AffiliateDepositType.TOTAL: {
                            depositValue.amount = 100;
                            depositValue.amountType = AffiliateDepositAmountType.PERCENT;

                            break;
                        }

                        case AffiliateDepositType.COMMISSION: {
                            depositValue.amount = 0;
                            depositValue.amountType = AffiliateDepositAmountType.PERCENT;

                            break;
                        }

                        case AffiliateDepositType.CUSTOM: {
                            depositValue.amountType = AffiliateDepositAmountType.ABSOLUTE;

                            break;
                        }
                        // No default
                    }

                    setIsOpenDepositAmountPopover(false);
                    setFieldValue("deposit", depositValue);
                    setDepositTypeSelectorText(getDepositTypeSelectorText(sellerCurrencySymbol, depositValue));
                };

                return (
                    <ContentLayout isWide isDisabled={isLoading} className="space-y-6">
                        <h2>Purchase Settings</h2>

                        <div className="space-y-4 divide-y divide-gray-light">
                            {isGranted(Permission.AGENTS_EXPERIENCES_SETTINGS_VIEW) &&
                            isAffiliateLoginEnabled &&
                            isDelegated ? (
                                <div
                                    className="flex space-x-10 py-12"
                                    data-testid="agent-purchase-settigns-form-products"
                                >
                                    <div
                                        className="flex max-w-[320px] flex-col"
                                        data-testid="agent-purchase-settigns-form-products"
                                    >
                                        <h3>Agent Permissions</h3>
                                        <p className="text-gray-darker">
                                            Choose which products or package product you want your Agent to have access
                                            to
                                        </p>
                                    </div>
                                    <div className="w-1/2 pl-8">
                                        <div className="flex flex-col space-y-2">
                                            <Label className="flex items-center justify-between">
                                                <span>Products</span>
                                                <SecondaryButton size="small" onClick={toggleExperienceSelectorModal}>
                                                    <PenIcon />
                                                </SecondaryButton>
                                            </Label>
                                            <SelectedProductList
                                                selectedProducts={getSelectionState(values.selectedExperiences)}
                                            />
                                            <ProductSelectorModal
                                                enableSelectAll
                                                value={getSelectionState(values.selectedExperiences)}
                                                isOpen={isOpenExperienceSelectorModal}
                                                title="Product Restrictions"
                                                description="Restrict the products where this equipment is used"
                                                objects={[ProductObject.EXPERIENCE, ProductObject.PACKAGE]}
                                                onClose={toggleExperienceSelectorModal}
                                                onConfirm={(value: SelectionState) =>
                                                    setFieldValue("selectedExperiences", getSelectedExperiences(value))
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                            <div className="space-x-12">
                                <div className="flex space-x-10 py-12">
                                    <div className="flex max-w-[320px] flex-col">
                                        <h3>Preferences</h3>
                                        <p className="text-gray-darker">
                                            Choose the communication and deposit preferences for your Agent
                                        </p>
                                    </div>
                                    <div className="flex w-1/2 flex-col space-y-2 pl-8">
                                        <Label>Communications</Label>
                                        <SwitchField
                                            isChecked={values.travelerNotification.enabled}
                                            onChange={(value: boolean) =>
                                                setFieldValue("travelerNotification.enabled", value)
                                            }
                                        >
                                            Send reservation emails to the customer
                                        </SwitchField>
                                        <SwitchField
                                            isChecked={values.affiliateNotification.enabled}
                                            onChange={(value: boolean) =>
                                                setFieldValue("affiliateNotification.enabled", value)
                                            }
                                        >
                                            Send a copy of reservation emails to this agent
                                        </SwitchField>
                                        <div className="space-y-2">
                                            <Label>Deposit Rules</Label>
                                            <span className="block">
                                                <SwitchField
                                                    isChecked={values.voucher}
                                                    onChange={(value: boolean) => setFieldValue("voucher", value)}
                                                >
                                                    This agent requires a voucher during purchase
                                                </SwitchField>
                                            </span>
                                            <span
                                                className="flex items-center space-x-1 text-gray-darker"
                                                data-testid="agent-purchase-settigns-form-deposit"
                                            >
                                                <SwitchField
                                                    isChecked={values.deposit.enabled}
                                                    onChange={(value: boolean) =>
                                                        setFieldValue("deposit.enabled", value)
                                                    }
                                                >
                                                    Prefill the deposit value
                                                </SwitchField>
                                                {values.deposit.enabled ? (
                                                    <>
                                                        <span> with </span>
                                                        <DepositTypeSelector
                                                            isOpen={isOpenDepositAmountPopover}
                                                            text={depositTypeSelectorText}
                                                            value={values.deposit}
                                                            errors={errors.deposit}
                                                            onFieldChange={setFieldValue}
                                                            onItemClick={handleDepositTypeChange}
                                                            onClose={() => {
                                                                setIsOpenDepositAmountPopover(
                                                                    !isOpenDepositAmountPopover,
                                                                );
                                                            }}
                                                        />
                                                    </>
                                                ) : null}
                                            </span>
                                            {values.deposit.enabled ? (
                                                <span className="block">
                                                    <SwitchField
                                                        isChecked={values.deposit.validate}
                                                        onChange={(value: boolean) =>
                                                            setFieldValue("deposit.validate", value)
                                                        }
                                                    >
                                                        Allow agent to override deposit
                                                    </SwitchField>
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <ContentLayout.Footer className="space-x-3">
                            <SubmitButton type="submit" disabled={!dirty} isLoading={isLoading} onClick={handleSubmit}>
                                Save
                            </SubmitButton>
                            <SecondaryButton type="reset" onClick={handleCancelClick}>
                                Cancel
                            </SecondaryButton>
                        </ContentLayout.Footer>

                        <ScrollToFieldError />
                    </ContentLayout>
                );
            }}
        </XolaFormik>
    );
};

interface DepositTypeSelectorProps {
    isOpen: boolean;
    text: string;
    value: AffiliateDeposit;
    errors?: any;
    onFieldChange: (field: string, value: any) => void;
    onItemClick: (depositType: AffiliateDepositType) => void;
    onClose: () => void;
}

const DepositTypeSelector = ({
    isOpen,
    text,
    value,
    errors,
    onFieldChange,
    onItemClick,
    onClose,
}: DepositTypeSelectorProps) => {
    const seller = useSeller();
    const locale = useLocalizationStore().getLocale();

    const sellerCurrencySymbol = getSymbol(seller.currency, locale);

    return (
        <InlineValuePopover
            text={text}
            placement="bottom"
            maxWidth={450}
            isOpen={isOpen}
            classNames={{ children: "!p-0", text: "no-translate text-blue" }}
            onClick={(event: any) => {
                event.stopPropagation();
                onClose();
            }}
            onClickOutside={onClose}
        >
            <PopoverList.Item
                name={AffiliateDepositType.CUSTOM}
                className="items-center justify-between px-4 py-2"
                onClickItem={() => {
                    onFieldChange("deposit.amountType", AffiliateDepositType.CUSTOM);
                }}
            >
                <span>Custom Amount</span>
                <div className="flex items-center space-x-1.5">
                    <Input
                        isError={errors?.amount}
                        className="w-20"
                        type="number"
                        value={value.amount ? value.amount : null}
                        onChange={(e: any) => {
                            onFieldChange("deposit.amount", Number(e.target.value));
                            // Update amount type to absolute (default), as deposit amount type remains as percent when changed from total value until applied
                            onFieldChange("deposit.amountType", AffiliateDepositAmountType.ABSOLUTE);
                        }}
                    />
                    <Select
                        name="algorithm"
                        className="!w-16 flex-none leading-p1"
                        value={value.amountType}
                        onChange={(e: any) => {
                            onFieldChange("deposit.amountType", e.target.value);
                        }}
                    >
                        <option value={AffiliateDepositAmountType.ABSOLUTE}>{sellerCurrencySymbol}</option>
                    </Select>
                    <Button
                        disabled={errors?.amount}
                        onClick={() => {
                            onItemClick(AffiliateDepositType.CUSTOM);
                        }}
                    >
                        Apply
                    </Button>
                </div>
            </PopoverList.Item>
            <PopoverList.Item
                name={AffiliateDepositType.COMMISSION}
                onClickItem={() => {
                    onItemClick(AffiliateDepositType.COMMISSION);
                }}
            >
                <span>Commission Amount</span>
            </PopoverList.Item>
            <PopoverList.Item
                name={AffiliateDepositType.TOTAL}
                onClickItem={() => {
                    onItemClick(AffiliateDepositType.TOTAL);
                }}
            >
                <span>Total value of booking</span>
            </PopoverList.Item>
        </InlineValuePopover>
    );
};

const getDepositTypeSelectorText = (sellerCurrencySymbol: string, deposit?: AffiliateDeposit) => {
    if (!deposit) {
        return AffiliateDepositTypeCopy[AffiliateDepositType.TOTAL];
    }

    deposit.amount = deposit.amount ?? 0;
    if (deposit?.depositType === AffiliateDepositType.CUSTOM) {
        let text = deposit.amount?.toString() ?? "0";
        text =
            deposit.amountType === AffiliateDepositAmountType.PERCENT ? `${text}%` : `${sellerCurrencySymbol}${text}`;

        return text;
    }

    return AffiliateDepositTypeCopy[deposit.depositType];
};
